import React, { useState, useRef, useEffect } from 'react';

/**
 * Agnostic info badge ("i") with a styled tooltip popover.
 * Opens on hover (desktop) and click/tap (mobile/touch); closes on outside
 * click or Escape. Opens below and right-aligned so it never clips a tile
 * corner. Reset typography (text-left / normal-case / font-normal) so it can
 * live inside uppercase or bold headers.
 *
 * Props:
 *  - text: tooltip content
 *  - className: extra classes on the wrapper (e.g. absolute positioning)
 *  - dotClassName: override the "i" dot styling (border/text color)
 *  - width: tooltip width utility (default 'w-56')
 */
const InfoTip = ({ text, className = '', dotClassName = '', width = 'w-56' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!text) return null;

  return (
    <span
      ref={ref}
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="Plus d'informations"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className={`inline-flex w-3.5 h-3.5 rounded-full border items-center justify-center text-[9px] font-bold leading-none cursor-help outline-none transition-colors ${
          dotClassName || 'border-gray-300 dark:border-gray-600 text-gray-400 hover:border-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
        }`}
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          className={`absolute top-full right-0 mt-1.5 ${width} rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-[11px] font-normal normal-case tracking-normal leading-snug text-gray-600 dark:text-gray-300 shadow-xl z-50 text-left`}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default InfoTip;
